/**
 * Screen 06 — set password. NOT skippable.
 *
 * This is the one screen in the set whose necessity the product argues for itself: a seeded
 * or invited credential was chosen by SOMEBODY ELSE, and until it is replaced at least two
 * people can sign in as this operator. Every compliance report Thunder produces carries the
 * name of whoever signed it, so an action nobody can pin to one person makes the report worth
 * less than the paper.
 *
 * The copy says that in three short sentences — who you are, what is true of this password,
 * what to do — rather than packing the justification into a subordinate clause. The earlier
 * wording ("while it is shared, the audit trail cannot attribute an action to a person") read
 * as though the OPERATOR had shared it, which is an accusation aimed at the one person in the
 * story who has done nothing wrong.
 *
 * The strength meter measures LENGTH AND VARIETY, and says which of the two is lacking.
 * A bare "weak/strong" verdict gives the user nothing to act on, so they add an
 * exclamation mark and try again.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { PASSWORD_MIN_LENGTH, PASSWORD_COMFORTABLE_LENGTH } from '@/lib/password-policy';

/*
 * Imported rather than declared. This screen refused under 10 while the profile screen
 * refused under 8 and the API accepts 8 — three numbers for one rule, so a nine-character
 * password was accepted in one place and refused in another. See `lib/password-policy.ts`.
 */
const MIN_LENGTH = PASSWORD_MIN_LENGTH;

export function SetPasswordScreen({
  email,
  onSubmit,
  onSignOut,
  busy,
  error,
  reason = 'issued',
}: {
  email: string;
  onSubmit: (next: string) => void;
  onSignOut: () => void;
  busy: boolean;
  error: string | null;
  /**
   * `issued` — an invited or seeded credential, which is SHARED until it is replaced.
   * `reset` — the operator followed their own reset link; nothing was issued to them.
   *
   * One screen, two arguments, because the audit-trail argument below is only true of the
   * first and a compliance product does not get to print it at people it does not apply to.
   */
  reason?: 'issued' | 'reset' | undefined;
}): ReactNode {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const strength = useMemo(() => score(next), [next]);
  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = next.length >= MIN_LENGTH && next === confirm;

  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1}>{reason === 'reset' ? 'Choose a new password.' : 'Set your own password.'}</h1>
      {reason === 'reset' ? (
        <p style={sub}>
          You followed a reset link for <span style={{ color: '#d4d4d8' }}>{email}</span>.
          Pick a new password and we&rsquo;ll put you back in the console — the link is spent
          either way, so finish here rather than closing the tab.
        </p>
      ) : (
        <p style={sub}>
          Signed in as <span style={{ color: '#d4d4d8' }}>{email}</span>. Someone else set
          this password, so the audit trail cannot yet tell your actions from theirs. Choose
          your own to continue.
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onSubmit(next);
        }}
        style={{ display: 'grid', gap: 18, marginTop: 32 }}
      >
        <label style={{ display: 'grid', gap: 7 }}>
          <span style={labelStyle}>New password</span>
          <input
            className="lm-si-input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            style={input}
            autoFocus
          />
          <span style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 3 }}>
            <span
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: '#232936',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${strength.pct}%`,
                  borderRadius: 2,
                  background: strength.colour,
                  boxShadow: `0 0 8px ${strength.colour}`,
                  transition: 'width 180ms ease, background 180ms ease',
                }}
              />
            </span>
            <span style={{ fontSize: 11, color: strength.colour, whiteSpace: 'nowrap' }}>
              {strength.hint}
            </span>
          </span>
        </label>

        <label style={{ display: 'grid', gap: 7 }}>
          <span style={labelStyle}>Confirm password</span>
          <input
            className="lm-si-input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={{ ...input, borderColor: mismatch ? '#f87171' : '#283041' }}
          />
          {mismatch && (
            <span style={{ fontSize: 11, color: '#fca5a5' }}>The two entries do not match.</span>
          )}
        </label>

        {error && (
          <p role="alert" style={errorNote}>
            <span style={errorDot} />
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!ready || busy}
          className="lm-si-amber"
          style={{
            minHeight: 48,
            marginTop: 4,
            border: 0,
            borderRadius: 10,
            font: 'inherit',
            fontSize: 15,
            fontWeight: 600,
            color: '#18181b',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            boxShadow: !ready || busy ? 'none' : '0 6px 26px rgba(245,158,11,0.3)',
            cursor: !ready || busy ? 'not-allowed' : 'pointer',
            opacity: !ready || busy ? 0.45 : 1,
          }}
        >
          {busy ? 'Saving…' : 'Set password and continue'}
        </button>

        <button
          type="button"
          onClick={onSignOut}
          className="lm-si-link"
          style={{
            background: 'none',
            border: 0,
            font: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
            padding: 0,
            textAlign: 'left',
            width: 'fit-content',
          }}
        >
          Sign out instead
        </button>
      </form>
    </section>
  );
}

/**
 * Length first, variety second — and the hint names whichever is missing.
 *
 * Deliberately not a dictionary or entropy estimator: those need a wordlist in the bundle
 * and still cannot see that a password is reused, which is the actual risk here. Length is
 * the honest lever to push.
 */
function score(pw: string): { pct: number; colour: string; hint: string } {
  if (pw.length === 0) return { pct: 0, colour: '#6b6b76', hint: `At least ${MIN_LENGTH} characters` };

  const classes =
    Number(/[a-z]/.test(pw)) + Number(/[A-Z]/.test(pw)) + Number(/\d/.test(pw)) + Number(/[^A-Za-z0-9]/.test(pw));

  if (pw.length < MIN_LENGTH) {
    return {
      pct: Math.round((pw.length / MIN_LENGTH) * 45),
      colour: '#f87171',
      hint: `${MIN_LENGTH - pw.length} more characters`,
    };
  }
  /*
   * ADVICE, NOT A GATE. The floor dropped from 10 to 8 to match the API and GoTrue — a
   * screen refusing what the server accepts produces a refusal the reader cannot appeal.
   * The meter still pushes toward longer, which is the honest lever and costs nothing.
   */
  if (pw.length < PASSWORD_COMFORTABLE_LENGTH) {
    return {
      pct: 55,
      colour: '#fbbf24',
      hint: `${PASSWORD_COMFORTABLE_LENGTH - pw.length} more would be safer`,
    };
  }
  if (classes <= 2 && pw.length < 16) {
    return { pct: 62, colour: '#fbbf24', hint: 'add a number or symbol' };
  }
  if (pw.length >= 16 || classes >= 3) {
    return { pct: 100, colour: '#34d399', hint: 'strong' };
  }
  return { pct: 78, colour: '#fbbf24', hint: 'acceptable' };
}

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';
const h1 = {
  fontWeight: 600,
  fontSize: 'clamp(30px, 3vw, 41px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: '#fafafa',
} as const;
const sub = { fontSize: 15, lineHeight: 1.6, margin: '14px 0 0', color: '#a1a1aa' } as const;
const labelStyle = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.13em',
  textTransform: 'uppercase' as const,
  color: '#8b8b95',
} as const;
const input = {
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: 46,
  padding: '0 13px',
  font: 'inherit',
  fontSize: 15,
  color: '#fafafa',
  background: '#12161f',
  border: '1px solid #283041',
  borderRadius: 10,
  outline: 'none',
} as const;
const errorNote = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  margin: 0,
  padding: '11px 13px',
  fontSize: 13.5,
  lineHeight: 1.5,
  color: '#fca5a5',
  background: 'rgba(248,113,113,0.09)',
  border: '1px solid rgba(248,113,113,0.28)',
  borderRadius: 10,
} as const;
const errorDot = {
  flex: 'none',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#f87171',
  boxShadow: '0 0 8px #f87171',
} as const;
